// THE SCOUT LANE: the survey it sums over its rounds, and the two refusals that used to
// share the survey's name — the tree-contract refusal and the thrown-survey leg.
// One module because the two refusals are only legible beside the survey they were split
// out of (rule 1 in the barrel's header).
// Split out of `../hive-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import {
  count,
  HIVE_EVENT_MAX_ITEMS,
  HIVE_EXCERPT_MAX_CHARS,
  HIVE_PATH_MAX_CHARS,
  line,
} from './bounds';
import { HiveEvent } from './kind';
import { HiveRoundPayloadSchema } from './round';
import { HiveSteerEntrySchema } from './steer';

/** Why a scout did not contribute a finding. */
export const HiveScoutFailure = {
  /** The box drained before it started — no work was attempted. */
  Aborted: 'aborted',
  /** It ran and exited non-zero. */
  Exit: 'exit',
  /** It ran past the ceiling and was reaped. */
  Timeout: 'timeout',
  /** It could not be run at all (spawn threw, seam rejected). */
  Error: 'error',
  /** It finished cleanly and said NOTHING AT ALL. The only real no-answer. */
  NoAnswer: 'no_answer',
  /**
   * Its stream was CUT mid-run and the retry did not recover it. Graded apart from
   * {@link HiveScoutFailure.NoAnswer} because the two look identical downstream and lead a
   * debugger to opposite places: `no_answer` points at the prompt and the model,
   * `stream_truncated` points at the endpoint.
   */
  StreamTruncated: 'stream_truncated',
  /** It finished cleanly and answered in PROSE. Its text still rides the brief (rule 2). */
  Prose: 'prose',
  /** It answered with JSON that is not a finding. */
  Schema: 'schema',
} as const;
export type HiveScoutFailure = (typeof HiveScoutFailure)[keyof typeof HiveScoutFailure];

/** One scout that did not contribute a finding. Conditional facts are nullable, never absent. */
export const ScoutFailureEntrySchema = z.object({
  id: line().describe('the scout id'),
  round: z.number().int().min(1).describe('the round it was dispatched in'),
  failure: z
    .enum(Object.values(HiveScoutFailure))
    .describe('hive.md rule 2: which rung of the return ladder it fell off.'),
  question: line().describe('the bounded question it was handed'),
  reason: line().nullable().describe('one bounded line naming the failure, or null'),
  exit_code: z.number().int().nullable().describe('exit code, or null if it never reached one'),
  transcript: z
    .string()
    .max(HIVE_PATH_MAX_CHARS)
    .nullable()
    .describe('box-local transcript path — what makes "four scouts answered in prose" openable'),
  transcript_bytes: z.number().int().nonnegative().nullable().describe('size of that transcript'),
  transcript_error: z
    .string()
    .max(HIVE_EXCERPT_MAX_CHARS)
    .nullable()
    .describe('why the transcript write was refused'),
});
export type ScoutFailureEntry = z.infer<typeof ScoutFailureEntrySchema>;

/**
 * THE SCOUT SURVEY, summed over its rounds (`apps/runner/src/modes/scout/survey/event.ts`
 * `surveyEvent`). Counts, questions and failure reasons only: a finding's facts are repo
 * content, and the audit chain is not where repo content belongs — the brief carries it,
 * to the model, redacted.
 *
 * Measured baseline (prod, 30 days to 2026-09-02): 604 surveys carrying `scouts`, of which
 * **167 were one scout in one round** — rule 5's strictly-worse-than-nothing case — and
 * 342 of 1,951 scouts answered in prose rather than the finding schema.
 */
export const ScoutSurveyPayloadSchema = HiveRoundPayloadSchema.extend({
  event: z.literal(HiveEvent.ScoutSurvey),
  // NO `max_rounds` (deleted 2026-09-16 with the round cap, #4240): rounds stop on the
  // no-new-questions detector. Historical rows still carry it; the object is not strict, so
  // they parse and the key is dropped.
  /** Concurrency width this survey ran at. */
  max_scouts: z
    .number()
    .int()
    .min(1)
    .describe(
      'hive.md rule 5: scouts reading at once (`DZ_SCOUT_MAX_SCOUTS`) — a width, never a count',
    ),
  /** Scouts whose finding parsed and reached the brief anchored. */
  answered: count().describe('hive.md rule 2 rung 1: findings that parsed. The best return.'),
  /**
   * Scouts that answered outside the envelope and were carried to the lead as untrusted
   * prose (rule 2 rung 2). NOT a loss and NOT a clean answer — nobody can tell those apart
   * from `answered` alone, and the plan looks identical either way.
   */
  unstructured: count().describe(
    'hive.md rule 2 rung 2: recovered as prose. Unanchored, unverifiable, still contributed.',
  ),
  /** Scouts that exited clean and said nothing at all — rule 2 rung 3, the only real no-answer. */
  no_answer: count().describe(
    'hive.md rule 2 rung 3: the only REAL no-answer. Full count, never derived from the capped list below.',
  ),
  /**
   * Raw tasks this survey’s handoffs became (#4090) — the writer half of the inlet
   * `apps/worker/src/fleet/survey-filing-ingest.ts` reads. Beside `no_answer` because
   * the two bracket a scout’s whole return: said nothing at all, or handed off work
   * nobody asked about. The count the producer FILED — every admissible handoff, one
   * per distinct piece of work (no count since 2026-09-16) — and 0 covers three
   * different facts one row must keep distinguishable: no handoffs authored, nothing the floor admitted, or a survey
   * that verified nothing and so may not file at all.
   */
  survey_filings: count().describe(
    'raw tasks filed off this survey’s handoffs (`raw_task_filed`, source `survey`). Every admissible handoff, duplicates collapsed.',
  ),
  /** Findings whose anchors were probed against the checkout and did not exist. */
  anchors_unverified: count().describe(
    'hive.md rule 7: `path:line` anchors the checkout disproved. The lead verifies; a report is a claim.',
  ),
  /** The unverified anchors themselves — model output the checkout proved is not repo content. */
  unverified_anchors: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('the anchors themselves. "3 anchors did not exist" is unactionable without them.'),
  /** Transcripts on disk for this survey. */
  transcripts: count().describe('transcripts written'),
  transcript_errors: count().describe(
    'transcript writes refused — the opposite fix from zero written',
  ),
  /** Size of the synthesised brief. The bytes, never the brief. */
  brief_chars: count().describe('size of the synthesised brief handed to the planner'),
  /** Open questions still open when the survey stopped (nothing new to ask, a steer, a drain). */
  open_questions: z
    .array(line())
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 3: gaps named and left undispatched. A smoothed-over gap ships a wrong plan.',
    ),
  open_questions_truncated: count().describe('open questions beyond the cap'),
  // NO `questions_dropped` (deleted 2026-09-16, #4240): no question is left undispatched by a
  // count — a round wider than the width queues.
  /** What item-wise salvage cost, summed across the survey — a scout drifting from the contract. */
  facts_dropped: count().describe('facts removed to bring a finding inside the schema'),
  anchors_dropped: count().describe('individual `path:line` anchors removed from surviving facts'),
  salvage_questions_dropped: count().describe('open questions removed during salvage'),
  falsified_dropped: count().describe('hive.md rule 11: falsified premises removed during salvage'),
  handoffs_dropped: count().describe('hive.md rule 10: handoffs removed during salvage'),
  prose_trimmed: count().describe('prose contributions trimmed to fit'),
  scout_failures: z
    .array(ScoutFailureEntrySchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe(
      'hive.md rule 2: WHY each scout failed. Capped; `failed` and `no_answer` are the true counts.',
    ),
  scout_failures_truncated: count().describe('failures beyond the cap'),
  /** Where each dispatched scout can be reached while it works. Producer: plan 102 slice 04. */
  steer: z
    .array(HiveSteerEntrySchema)
    .max(HIVE_EVENT_MAX_ITEMS)
    .describe('hive.md rule 9: address -> scout. Empty is a lane whose leaves have no channel.'),
  steer_truncated: count().describe('addresses beyond the cap'),
  /**
   * THE ONE OPTIONAL FIELD IN THIS WHOLE CONTRACT (`./hive-event/`), and it is optional
   * because ABSENT MEANS THE LEAD DID NOT FAIL — not because some generation of producer
   * omitted it.
   *
   * Everything else here is a count whose zero is a measurement, or a conditional fact
   * carried as `null`. This is neither: a lead brain that faulted is a survey-level failure
   * (`ok` is false beside it) and its reason is a string that either exists or does not.
   * Making it nullable would demand every healthy survey write `lead_failure: null`, which
   * is a field whose only value is "no", on every row, forever.
   */
  lead_failure: z
    .object({ reason: line() })
    .optional()
    .describe(
      'the lead brain faulted. ABSENT means the lead did not fail — the one absence in this module that is a fact.',
    ),
});
export type ScoutSurveyPayload = z.infer<typeof ScoutSurveyPayloadSchema>;

// ─── scout lane: the two refusals that used to share the survey's name ───────

/**
 * Which lane refused. Restated here rather than imported because `@developerz/domain`
 * imports nothing internal and `RunnerLane` lives in `apps/runner/src/tree-contract.ts`;
 * this module is the one a mothership read seam can reach.
 *
 * ENUMERATED FROM `RunnerLane`, which is itself `Record`-enforced by `LANE_TREE_CONTRACT`:
 * scout, coding, debugger, review. A lane added there without a member here fails this
 * schema at the emit site — loudly, in the producer's own unit test.
 */
export const HiveLane = {
  /** Plans the work. Read-only; its `file:line` anchors become task envelopes. */
  Scout: 'scout',
  /** Writes the code. Branches from the tree it is given. */
  Coding: 'coding',
  /** Reproduces and diagnoses a failure. Branches and writes, exactly like coding. */
  Debugger: 'debugger',
  /** Reviews a pull request. Read-only; its findings are about the reviewed commit. */
  Review: 'review',
} as const;
export type HiveLane = (typeof HiveLane)[keyof typeof HiveLane];

/** WHICH tree a lane is entitled to. Mirrors `TreeRequirement['kind']`. */
export const HiveTreeRequirement = {
  /** The repository's default branch at its current head. */
  DefaultBranch: 'default_branch',
  /** The pull request's branch, at the head commit the review was asked about. */
  ReviewedHead: 'reviewed_head',
} as const;
export type HiveTreeRequirement = (typeof HiveTreeRequirement)[keyof typeof HiveTreeRequirement];

/** WHICH HALF failed, when a lane could not be given the tree it requires. Mirrors `TreeGap`. */
export const HiveTreeGap = {
  /** No freshness verdict was produced at all. Absence is not proof. */
  NotProven: 'not_proven',
  /** The repository's default branch could not be determined. */
  BaseUnresolved: 'base_unresolved',
  /** The required ref could not be fetched — no credential, or the fetch failed. */
  FetchFailed: 'fetch_failed',
  /** The tree's own HEAD could not be read. */
  HeadUnreadable: 'head_unreadable',
  /** HEAD is a different ref than required, and this lane will not move it there. */
  WrongRef: 'wrong_ref',
} as const;
export type HiveTreeGap = (typeof HiveTreeGap)[keyof typeof HiveTreeGap];

/** Why `ensureCheckout` handed back a tree it could not prove current. Mirrors `CheckoutStaleReason`. */
export const HiveTreeStaleReason = {
  NoToken: 'no_token',
  NoWorktree: 'no_worktree',
  NoBase: 'no_base',
  FetchFailed: 'fetch_failed',
  DirtyTree: 'dirty_tree',
  Diverged: 'diverged',
  /** An untracked file stands where the base carries a path, so git would abort (#3644). */
  UntrackedCollision: 'untracked_collision',
  CheckoutFailed: 'checkout_failed',
} as const;
export type HiveTreeStaleReason = (typeof HiveTreeStaleReason)[keyof typeof HiveTreeStaleReason];

/**
 * THE LANE REFUSED BEFORE DISPATCHING: the checkout is not the tree the lane requires.
 *
 * ITS OWN EVENT NAME, and that is the whole point of this schema. 69 of 685 prod
 * `scout_survey` rows are this payload — it shares nothing with a survey round but the
 * word `event` — and one name meaning two shapes is why the consumer at
 * `packages/db/src/queries/admin.ts` had to say in prose that "`scout_survey` is not one
 * payload shape" and grade every rate against a denominator it had to fence by key.
 *
 * The historical rows keep the old name and land in the read seam's `unparseable` bucket
 * with a named reason. That is history, correctly filed — not a shape to keep parsing.
 *
 * A refusal costs the BRIEF and never the plan: the brain plans on its own grounding, and
 * a survey of a leftover coding branch would mint `file:line` anchors for code that is not
 * on the default branch, so the plan built on them is confidently wrong rather than
 * obviously ungrounded.
 */
export const ScoutTreeGapPayloadSchema = z.object({
  event: z.literal(HiveEvent.ScoutTreeGap),
  /** Always false. The event exists only on the refusal path. */
  ok: z.literal(false).describe('a tree-gap event is a refusal; there is no ok variant'),
  lane: z.enum(Object.values(HiveLane)).describe('the lane that refused'),
  requires: z
    .enum(Object.values(HiveTreeRequirement))
    .describe('the tree this lane is entitled to, from its declared contract'),
  tree_gap: z
    .enum(Object.values(HiveTreeGap))
    .describe('WHICH HALF of the proof failed. Each names a different next action.'),
  tree_cause: line(1_000).describe(
    'one plain sentence for an operator reading a run timeline — never the code path that noticed',
  ),
  /** `null` when the refusal came from no refresh verdict at all rather than a declined one. */
  tree_reason: z
    .enum(Object.values(HiveTreeStaleReason))
    .nullable()
    .describe('how the refresh declined, or null when no refresh verdict was produced'),
  /** `null` when HEAD was unreadable. A ref name off the box's own checkout, never content. */
  tree_found: z
    .string()
    .trim()
    .min(1)
    .max(HIVE_PATH_MAX_CHARS)
    .nullable()
    .describe('the ref the checkout was actually holding, or null when HEAD could not be read'),
  /**
   * WHICH git step refused and what it said — the refresh's own `detail` (#3531).
   *
   * `tree_reason` above names a CLASS and `tree_cause` is a static sentence per gap, so
   * before this field a `checkout_failed` could be a `git status` that would not run or a
   * `git checkout` git itself refused, and the timeline could not tell them apart: 34
   * tasks over 5 days, one shape, no cause. Redacted at the producer (`gitExcerpt` runs
   * `redactGitOutput` over the whole string first) and bounded again on the way in.
   *
   * `null` when the refusal came from no refresh verdict at all — the same posture
   * `tree_reason` takes, and for the same reason: absence is a fact the box wrote.
   *
   * `.nullable()`, deliberately NOT `.optional()`, so a row written before this field
   * existed does not parse. That is rule 3 of `hive-event.ts` and it is guarded by name
   * (`hive-event-schemas.test.ts` → "exactly one optional field in the whole module"),
   * whose comment anticipates this exact edit: "a future `.optional()` added to make an
   * old row parse fails this test by name". Nothing reads those rows through this schema
   * — the only consumer is the PRODUCER at write time, and `gradeHiveRates` drops
   * `scout_tree_gap` before parsing — so the cost is a bucket, not a blind operator.
   */
  tree_detail: z
    .string()
    .trim()
    .min(1)
    .max(HIVE_EXCERPT_MAX_CHARS)
    .nullable()
    .describe('the git step that refused and its redacted output, or null when there was none'),
});
export type ScoutTreeGapPayload = z.infer<typeof ScoutTreeGapPayloadSchema>;

/**
 * THE SURVEY ITSELF THREW. The run continues on the brain's own grounding; this row is the
 * only record that a survey was attempted and produced nothing.
 *
 * Its own event name for the same reason {@link ScoutTreeGapPayloadSchema} has one: it was
 * the THIRD shape emitted under `scout_survey`, and a read seam that must try three shapes
 * before deciding a row is unparseable cannot report a rate about any of them.
 */
export const ScoutSurveyFailedPayloadSchema = z.object({
  event: z.literal(HiveEvent.ScoutSurveyFailed),
  /** Always false. The event exists only on the throw path. */
  ok: z.literal(false).describe('a failed-survey event is a failure; there is no ok variant'),
  error: line(HIVE_EXCERPT_MAX_CHARS).describe(
    'the redacted, bounded reason the survey threw. An unaudited refusal did not happen.',
  ),
});
export type ScoutSurveyFailedPayload = z.infer<typeof ScoutSurveyFailedPayloadSchema>;
